import { fertilityProductFormats } from 'constants/products';
import { applicationTypes, scriptTypes } from 'constants/scripts';
import { UNIT_TYPES } from 'constants/unitTypes';

import { getNominalWeightByCropId } from 'crops/selectors';
import { getApplicationTypeForScriptType, getScriptTypeForScript } from 'scripts/selectors';
import {
  getApplicationsForZone, getApplicationsForZoneByType
} from 'scripts/zones/selectors';
import {
  getSeedApplicationsForZone
} from 'scripts/zones/planting-selectors';
import {
  getFertilizerApplicationsForZone,
  getStabilizerApplicationsForZone
} from 'scripts/zones/fertility-selectors';
import { getPlantingPlanParams } from 'planting-plan-form/selectors';
import { convert, convertByFormat } from 'util/unitConversion';
import { get, has, isEmpty, omit, pick } from 'lodash';
import { getFormatFromZones } from 'products/selectors';

export const parseScriptForV1Save = (script) => {
  const { meta, zones } = script;
  script.name = meta.name;
  script.crop = meta.crop;

  const features = Object.keys(zones).map((zoneId) => {
    let feature = {};

    const zone = zones[zoneId];
    const { meta } = zone;

    const { product: seedProduct, rate: seedRate, 'target-yield': seedTargetYield }
      = getSeedApplicationsForZone(zone)[0];
    const fertilizerApplicationsForZone = getFertilizerApplicationsForZone(zone);
    let liquidRate;
    if (fertilizerApplicationsForZone.length) {
      liquidRate = fertilizerApplicationsForZone[0].rate;
    }

    const prunedSeedProduct = pick(seedProduct, ['brand','id','name']);

    feature.properties = {
      description: meta.index + 1, // for display so it doesn't start from 0
      liquid: get(liquidRate, 'q') ? { 'q': liquidRate.q, 'units': UNIT_TYPES.gal_ac } : undefined,
      product: isEmpty(prunedSeedProduct) ? null : prunedSeedProduct,
      rate: get(seedRate, 'q'),
      'target-yield': get(seedTargetYield, 'q'),
      'zone-id': zoneId
    };

    feature = Object.assign({}, zone.geoJson, feature);
    return feature;
  });

  script.summary.zones = script.summary.zones.map((zone) => {
    if (!zone.area) {
      return zone;
    }
    const acres = convert({ from: zone.area, to: { u: UNIT_TYPES.acre }});
    return {
      id: zone.id,
      acres: acres.q
    };
  });

  script.season = get(script, 'cropping-year', script.season);

  script.geometry = {
    crs: { type: 'name', properties: { name: 'EPSG:4326' } },
    type: 'FeatureCollection',
    features
  };
  // TODO: put attributes & activity-uuid back in for v2 save!

  return omit(script, [
    'field-id',
    'field-uuid',
    'meta',
    'zones',
    'zoneHistory',
    'attributes',
    'activity-type',
    'activity-uuid',
    'cropping-year'
  ]);
};

export const buildFertilityZoneProperties = ({ zone, scriptType }) => {
  const { meta } = zone;
  const applicationType = getApplicationTypeForScriptType(scriptType);
  const [fertilizerApplication] = getApplicationsForZoneByType({ zone, applicationType });
  const { incorporationMethod, product, rate } = fertilizerApplication;
  let format = getFormatFromZones([zone]);
  let stabilizer = getStabilizerApplicationsForZone(zone);
  stabilizer = stabilizer && stabilizer[0];
  if(stabilizer && stabilizer.rate) {
    stabilizer = stabilizer.rate.q ? stabilizer : null;
  }
  let zoneProps = {
    description: meta.index + 1, // for display so it doesn't start from 0
    'incorporation-method': incorporationMethod || '',
    product: pick(product, ['brand','id','name']),
    rate: convertByFormat({ format, from: rate }),
    'zone-id': meta.zoneId,
    stabilizer,
    'is-check-strip': meta.isCheckStrip ?? false
  };
  if (zoneProps.rate.u === UNIT_TYPES.l_ha) {
    zoneProps.rate.u = 'liter/hectare';
  }
  return zoneProps;

};

export const buildPlantingZoneProperties = ({ cropId, getState, zone }) => {
  const { meta } = zone;
  const {
    [applicationTypes.FERTILIZER]: liquidApplications,
    [applicationTypes.SEED]: seedApplications
  } = getApplicationsForZone(zone);

  const seedApplication = seedApplications[0];
  const { product, rate, 'target-yield': targetYield } = seedApplication;
  targetYield.q = targetYield.q || 0;
  const liquidRate = get(liquidApplications, '0.rate');
  const cropNominalWeight = getNominalWeightByCropId(getState(), cropId);

  let convertedYield = convert({ cropNominalWeight, from: targetYield, to: { u: UNIT_TYPES.kilogram_hectare } });
  convertedYield.u = 'kg/hectare';

  const convertedLiquid = Number.isFinite(get(liquidRate, 'q'))
    ? convertByFormat({ format: fertilityProductFormats.WET, from: liquidRate })
    : null;
  if (convertedLiquid && (convertedLiquid.u === UNIT_TYPES.l_ha)) {
    convertedLiquid.u = 'liter/hectare';
  }

  const baseProduct = pick(product, ['brand','id','name']);

  return {
    description: meta.index + 1, // for display so it doesn't start from 0
    liquid: convertedLiquid,
    product: baseProduct,
    rate: convert({ cropId, from: rate, to: { u: UNIT_TYPES.seed_hectare } }),
    'target-yield': convertedYield,
    'zone-id': meta.zoneId,
    'is-check-strip': meta.isCheckStrip ?? false
  };
};


export const buildZoneProperties = ({ cropId, getState, scriptType, zone }) => {
  switch (scriptType) {
    case scriptTypes.FERTILITY:
    case scriptTypes.APPLICATION:
      return buildFertilityZoneProperties({ getState, zone, scriptType });
    case scriptTypes.PLANTING:
      return buildPlantingZoneProperties({ cropId, getState, zone});
    default:
      return null;
  }
};

export const parseScriptForV2Save = ({ getState, script }) => {
  const scriptType = getScriptTypeForScript(script);

  let productsToSave;
  if (scriptType === scriptTypes.APPLICATION) {
    const state = getState();
    const plantingParams = getPlantingPlanParams(state);
    script['activity-type'] = plantingParams.activityType;

    if (state.plantingPlan.params.products && state.plantingPlan.params.products.APPLICATION_v2) {
      productsToSave = state.plantingPlan.params.products.APPLICATION_v2.map((value) => {
        return {
          id: value
        };
      });
    }
  }
  const { meta, zones } = script;
  const cropId = get(meta, 'cropId');

  script.name = meta.name;
  script.attributes = pick(meta, ['crop']);

  const features = Object.keys(zones).map((zoneId) => {
    let feature = {};
    const zone = zones[zoneId];

    feature.properties = buildZoneProperties({ cropId, getState, scriptType, zone });

    feature.properties['chemical-mix'] = productsToSave;

    if (productsToSave && productsToSave.length > 0) {
      feature.properties.product = productsToSave[0]; // 1 and only 1 product is required
    }
    feature = Object.assign({}, zone.geoJson, feature);
    return feature;
  });

  script.summary.zones = script.summary.zones.map((zone) => {
    if (!zone.area) {
      return zone;
    }
    const acres = convert({ from: zone.area, to: { u: UNIT_TYPES.acre }});
    return {
      id: zone.id,
      acres: acres.q
    };
  });

  script.geometry = {
    crs: { type: 'name', properties: { name: 'EPSG:4326' } },
    type: 'FeatureCollection',
    features
  };
  // @todo: remove densitycurveField
  return omit(script, [
    'field-id',
    'field-uuid',
    'meta',
    'zones',
    'zoneHistory',
    'densityCurveField',
    'highestEconomicField',
    'summary.planting',
    'program-instance-id'
  ]);
};

export const parseScriptForSave = ({ getState, script }) => {
  if (has(script, 'activity-type')) {
    return parseScriptForV2Save({ getState, script }); // new script from template call
  }
  return parseScriptForV1Save({ getState, script }); // existing script style
};
